import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Plus, Sparkles, ArrowRight, Search, Users, Lock, Store, Wrench,
    CheckCircle2, Wand2, Trash2, Star, ShieldCheck,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal";
import { useAuth } from "@/lib/AuthContext";

/* ================================================================
   Nexus One · Skill Builder — Studio list (Phase 5-A · D1 reskin)
   Dark Palantir/Gotham palette · violet accents · glassy cards.
   ================================================================ */

const TABS = [
    { key: "my",          label: "My Skills",         icon: Wrench },
    { key: "installed",   label: "Installed on Team", icon: Users },
    { key: "marketplace", label: "Marketplace",       icon: Store },
];

const LOCKED_TABS = [
    { key: "marketplace", label: "Marketplace", icon: Store },
];

const CATEGORIES = ["all", "executive", "legal", "sales", "hr", "ops", "finance", "marketing", "engineering", "other"];

export default function SkillStudio() {
    const { user } = useAuth();
    const isOwner = user?.role === "platform_owner";
    const [access, setAccess] = useState(null);      // /access/summary payload
    const canAuthor = isOwner || access?.owner || (access?.usage?.skill_author?.cap === null);
    const accessResolved = access !== null;
    const authoringLocked = accessResolved && !canAuthor;

    const [tab, setTab] = useState("my");
    const [skills, setSkills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [category, setCategory] = useState("all");
    const [q, setQ] = useState("");

    // Load access summary once so we know if the user can author.
    useEffect(() => {
        api.get("/access/summary").then((r) => setAccess(r.data)).catch(() => setAccess({}));
    }, []);

    // If the user is locked, force them onto the Marketplace tab.
    useEffect(() => {
        if (authoringLocked && (tab === "my" || tab === "installed")) {
            setTab("marketplace");
        }
    }, [authoringLocked, tab]);

    const load = async () => {
        setLoading(true);
        try {
            const params = { tab };
            if (category !== "all") params.category = category;
            if (q.trim()) params.q = q.trim();
            const { data } = await api.get("/custom-skills", { params });
            setSkills(data.skills || []);
            setError("");
        } catch (e) {
            setError(e.response?.data?.detail || "Could not load skills.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (accessResolved) load(); /* eslint-disable-next-line */ }, [tab, category, accessResolved]);

    const filtered = useMemo(() => {
        if (!q.trim()) return skills;
        const s = q.toLowerCase();
        return skills.filter((sk) =>
            (sk.name || "").toLowerCase().includes(s) ||
            (sk.description || "").toLowerCase().includes(s)
        );
    }, [skills, q]);

    const counts = useMemo(() => ({
        total: skills.length,
        active: skills.filter((s) => s.status === "active").length,
        draft: skills.filter((s) => s.status === "draft").length,
        approval: skills.filter((s) => s.approval_required).length,
    }), [skills]);

    return (
        <div className="h-full overflow-y-auto atlas-scrollbar" data-testid="skill-studio-page">
            {/* Sprint J-3-G · avoid a flash of authoring UI before the access
                gate resolves. This gates hero copy, tabs, and CTAs behind a
                skeleton so free-tier users never see "Build a new skill"
                first, then flip to "Upgrade to Build" a heartbeat later. */}
            {!accessResolved && (
                <div className="max-w-6xl mx-auto p-8 space-y-4" data-testid="skill-studio-loading">
                    <div className="h-6 w-40 rounded" style={{ background: "var(--atlas-bg-highlight)" }} />
                    <div className="h-12 w-3/4 rounded" style={{ background: "var(--atlas-bg-highlight)" }} />
                    <div className="h-5 w-1/2 rounded" style={{ background: "var(--atlas-bg-highlight)" }} />
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
                        {[0,1,2,3,4,5].map(i => (
                            <div key={i} className="h-32 rounded-xl" style={{ background: "var(--atlas-bg-highlight)" }} />
                        ))}
                    </div>
                </div>
            )}
            {accessResolved && (<>
            <div className="max-w-6xl mx-auto px-8 py-10">
                {/* -------- HERO -------- */}
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <span className="atlas-hero-eyebrow" data-testid="skill-studio-eyebrow">
                            <Sparkles className="w-3 h-3" strokeWidth={2.5} />
                            {authoringLocked ? "Skill Marketplace" : "Phase 5-A · Skill Builder"}
                        </span>
                        <h1
                            className="atlas-text-gradient mt-4 text-[36px] sm:text-[44px] font-semibold leading-[1.05] tracking-tight"
                            data-testid="skill-studio-heading"
                        >
                            {authoringLocked ? (
                                <>Browse and install skills curated<br />by Nexus One and your team.</>
                            ) : (
                                <>Build no-code AI skills.<br />Deploy them to your Workforce.</>
                            )}
                        </h1>
                        <p
                            className="mt-3 text-[14.5px] max-w-2xl leading-relaxed"
                            style={{ color: "var(--atlas-text-secondary)" }}
                        >
                            {authoringLocked
                                ? "Install curated legal, ops, and executive skills onto any activated Workforce Member. Upgrade to build your own reusable AI instructions."
                                : "Design reusable AI instructions once, install them on any hired Workforce Member, and let the digital workforce execute them on demand — with optional human approval gates."}
                        </p>
                    </div>
                    {authoringLocked ? (
                        <button
                            onClick={() => window.dispatchEvent(new CustomEvent("wfos:paywall", { detail: {
                                error: "paywall_required",
                                reason: "paywall_required",
                                feature: "skill_author",
                                message: "Authoring Custom Skills requires a paid plan. Browsing and installing curated skills is free.",
                                upgrade_sku_hint: "bundle_starter_monthly",
                                trial_available: false,
                                current: 0, cap: 0, tier: access?.tier || "free",
                            }}))}
                            data-testid="skillstudio-upgrade-cta"
                            className="atlas-btn-primary inline-flex items-center gap-1.5 rounded-lg px-4 h-11 text-[13.5px]"
                            style={{
                                background: "var(--atlas-gradient-btn)",
                                boxShadow: "var(--atlas-shadow-btn)",
                            }}
                        >
                            <Lock className="w-4 h-4" strokeWidth={2.25} />
                            Upgrade to Build
                        </button>
                    ) : (
                        <Link
                            to="/app/skills/new"
                            data-testid="skill-studio-build-new"
                            className="atlas-btn-primary inline-flex items-center gap-1.5 rounded-lg px-4 h-11 text-[13.5px]"
                        >
                            <Plus className="w-4 h-4" strokeWidth={2.25} />
                            Build a new skill
                        </Link>
                    )}
                </div>

                {/* -------- KPI STRIP -------- */}
                <div
                    className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3"
                    data-testid="skill-studio-kpis"
                >
                    <KpiCard label="Total skills" value={counts.total} testid="kpi-total" />
                    <KpiCard label="Active" value={counts.active} accent="success" testid="kpi-active" />
                    <KpiCard label="In draft" value={counts.draft} accent="warning" testid="kpi-draft" />
                    <KpiCard label="Approval-gated" value={counts.approval} accent="primary" testid="kpi-approval" />
                </div>

                {/* -------- TABS -------- */}
                <div
                    className="mt-8 flex flex-wrap items-center gap-1 border-b"
                    style={{ borderColor: "var(--atlas-border)" }}
                    data-testid="skill-studio-tabs"
                >
                    {(authoringLocked ? LOCKED_TABS : TABS).map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.key;
                        return (
                            <button
                                key={t.key}
                                onClick={() => setTab(t.key)}
                                data-testid={`skill-tab-${t.key}`}
                                className="relative inline-flex items-center gap-1.5 px-4 h-10 text-[13px] font-medium transition-colors"
                                style={{
                                    color: active ? "var(--atlas-text-primary)" : "var(--atlas-text-muted)",
                                }}
                            >
                                <Icon
                                    className="w-3.5 h-3.5"
                                    strokeWidth={2}
                                    style={{ color: active ? "var(--atlas-primary-hover)" : "currentColor" }}
                                />
                                {t.label}
                                {active && (
                                    <span
                                        className="absolute left-2 right-2 -bottom-px h-[2px] rounded-full"
                                        style={{
                                            background: "var(--atlas-gradient-btn)",
                                            boxShadow: "0 0 12px var(--atlas-primary-glow)",
                                        }}
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* -------- FILTERS -------- */}
                <div className="mt-5 flex items-center gap-2 flex-wrap">
                    <div className="relative">
                        <Search
                            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
                            strokeWidth={2}
                            style={{ color: "var(--atlas-text-muted)" }}
                        />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search skills…"
                            data-testid="skill-search"
                            className="rounded-lg pl-8 pr-3 h-9 text-[13px] w-64"
                        />
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                        {CATEGORIES.map((c) => {
                            const active = category === c;
                            return (
                                <button
                                    key={c}
                                    onClick={() => setCategory(c)}
                                    data-testid={`skill-category-${c}`}
                                    className="px-2.5 h-7 rounded-full text-[11.5px] font-medium border transition-colors"
                                    style={
                                        active
                                            ? {
                                                  background: "var(--atlas-primary-muted)",
                                                  borderColor: "rgba(139, 92, 246, 0.55)",
                                                  color: "var(--atlas-primary-hover)",
                                              }
                                            : {
                                                  background: "transparent",
                                                  borderColor: "var(--atlas-border-strong)",
                                                  color: "var(--atlas-text-tertiary)",
                                              }
                                    }
                                >
                                    {c}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {error && (
                    <div
                        className="mt-6 rounded-lg p-3 text-[13px]"
                        style={{
                            background: "var(--atlas-danger-muted)",
                            border: "1px solid rgba(239, 68, 68, 0.45)",
                            color: "#fca5a5",
                        }}
                        data-testid="skill-studio-error"
                    >
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="mt-10 text-[13px]" style={{ color: "var(--atlas-text-muted)" }}>
                        Loading skills…
                    </div>
                ) : filtered.length === 0 ? (
                    <EmptyState tab={tab} />
                ) : (
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="skill-grid">
                        {filtered.map((s) => (
                            <SkillCard key={s.id} skill={s} tab={tab} onChanged={load} canAuthor={canAuthor} />
                        ))}
                    </div>
                )}
                <div className="h-16" />
            </div>
            </>)}
        </div>
    );
}

function KpiCard({ label, value, accent = "neutral", testid }) {
    const accentColor =
        accent === "success" ? "#34d399" :
        accent === "warning" ? "#fbbf24" :
        accent === "primary" ? "var(--atlas-primary-hover)" :
                               "var(--atlas-text-primary)";
    return (
        <div className="atlas-card p-4" data-testid={testid}>
            <div className="atlas-label" style={{ color: "var(--atlas-text-muted)" }}>{label}</div>
            <div className="mt-2 text-[26px] font-semibold tabular-nums leading-none" style={{ color: accentColor }}>
                {value}
            </div>
        </div>
    );
}

function EmptyState({ tab }) {
    if (tab === "marketplace") {
        return (
            <div className="mt-10 atlas-card p-10 text-center" data-testid="skill-studio-empty-marketplace">
                <IllusOrb icon={Store} />
                <div className="mt-4 text-[18px] font-semibold" style={{ color: "var(--atlas-text-primary)" }}>
                    Marketplace is quiet.
                </div>
                <p className="mt-2 text-[13px] max-w-md mx-auto" style={{ color: "var(--atlas-text-tertiary)" }}>
                    No public skills yet. Build one and publish it — approved skills earn placement here.
                </p>
            </div>
        );
    }
    if (tab === "installed") {
        return (
            <div className="mt-10 atlas-card p-10 text-center" data-testid="skill-studio-empty-installed">
                <IllusOrb icon={Users} />
                <div className="mt-4 text-[18px] font-semibold" style={{ color: "var(--atlas-text-primary)" }}>
                    No skills installed yet.
                </div>
                <p className="mt-2 text-[13px] max-w-md mx-auto" style={{ color: "var(--atlas-text-tertiary)" }}>
                    Open a hired Workforce Member's page → Custom skills → Install a skill.
                </p>
            </div>
        );
    }
    return (
        <div className="mt-10 atlas-card p-10 text-center" data-testid="skill-studio-empty-my">
            <IllusOrb icon={Wand2} />
            <div className="mt-4 text-[18px] font-semibold" style={{ color: "var(--atlas-text-primary)" }}>
                You haven't built a skill yet.
            </div>
            <p className="mt-2 text-[13px] max-w-md mx-auto" style={{ color: "var(--atlas-text-tertiary)" }}>
                Skills are reusable AI instructions you can install on any hired Workforce Member. Start with a template from the Marketplace or build one from scratch.
            </p>
            <Link
                to="/app/skills/new"
                className="atlas-btn-primary mt-5 inline-flex items-center gap-1 rounded-lg px-4 h-10 text-[13px]"
                data-testid="skill-studio-empty-cta"
            >
                Build your first skill <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
            </Link>
        </div>
    );
}

function IllusOrb({ icon: Icon }) {
    return (
        <div
            className="mx-auto w-16 h-16 rounded-2xl grid place-items-center"
            style={{
                background:
                    "radial-gradient(circle at 30% 25%, rgba(167,139,250,0.55) 0%, rgba(139,92,246,0.22) 45%, rgba(6,182,212,0.10) 75%, transparent 100%)",
                border: "1px solid rgba(139,92,246,0.45)",
                boxShadow:
                    "inset 0 1px 0 rgba(255,255,255,0.10), 0 0 48px -8px var(--atlas-primary-glow)",
            }}
        >
            <Icon
                className="w-7 h-7"
                strokeWidth={1.6}
                style={{ color: "var(--atlas-primary-hover)" }}
            />
        </div>
    );
}

function SkillCard({ skill, tab, onChanged, canAuthor = true }) {
    const [running, setRunning] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const isSeed = skill.is_system_seed;
    const isCurated = skill.curated_by === "nexus_one";
    const roleChips = (skill.eligible_agent_roles || []).slice(0, 3);
    const isPrivate = skill.visibility === "private";
    // Locked users see NO write actions on ANY card — delete + activate are hidden.
    const canDelete = canAuthor && tab === "my" && !isSeed && !isCurated;
    const activate = async () => {
        setRunning(true);
        try {
            await api.post(`/custom-skills/${skill.id}/activate`);
            toast.success("Skill activated.");
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Activation failed.");
        } finally {
            setRunning(false);
        }
    };
    const confirmDelete = async () => {
        setDeleting(true);
        try {
            await api.delete(`/custom-skills/${skill.id}`);
            toast.success("Skill deleted.");
            setDeleteOpen(false);
            onChanged?.();
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Delete failed.");
        } finally {
            setDeleting(false);
        }
    };
    return (
        <div className={`atlas-card atlas-card--hoverable p-4 flex flex-col ${isCurated ? "atlas-card--elevated" : ""}`}
             style={isCurated ? { borderColor: "rgba(139,92,246,0.55)" } : undefined}
             data-testid={isCurated ? "curated-skill-card" : `skill-card-${skill.slug}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    {/* Title row — truncates cleanly, no chip inside so nothing clips. */}
                    <div
                        className="text-[15px] font-semibold truncate flex items-center gap-1.5"
                        style={{ color: "var(--atlas-text-primary)" }}
                    >
                        {skill.icon && <span className="flex-shrink-0">{skill.icon}</span>}
                        <span className="truncate">{skill.name}</span>
                    </div>
                    {/* Badge row — sits below the title so pills never clip on
                        narrow cards. */}
                    {(isCurated || isSeed) && (
                        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                            {isCurated && (
                                <span
                                    className="text-[9px] px-1.5 py-0.5 rounded-full whitespace-nowrap inline-flex items-center gap-1 font-semibold uppercase tracking-widest"
                                    style={{
                                        background: "var(--atlas-gradient-btn)",
                                        color: "#fff",
                                        border: "1px solid rgba(139,92,246,0.55)",
                                        boxShadow: "var(--atlas-shadow-btn)",
                                    }}
                                    title="Curated by Nexus One"
                                    data-testid="curated-skill-badge"
                                >
                                    <Star className="w-2.5 h-2.5" strokeWidth={2} fill="currentColor" />
                                    Curated by Nexus One
                                </span>
                            )}
                            {isSeed && !isCurated && (
                                <span className="atlas-pill atlas-pill--primary text-[9px] px-1.5 py-0.5">
                                    Template
                                </span>
                            )}
                        </div>
                    )}
                    <div
                        className="mt-0.5 text-[10.5px] font-semibold uppercase tracking-widest truncate"
                        style={{ color: "var(--atlas-text-muted)" }}
                    >
                        {skill.category} · v{skill.version} · {skill.status}
                    </div>
                </div>
                {skill.approval_required && (
                    <span
                        className="atlas-pill atlas-pill--warning text-[9.5px] whitespace-nowrap"
                        title={`Approval by ${skill.approval_role}`}
                    >
                        <Lock className="w-2.5 h-2.5" strokeWidth={2.5} /> {skill.approval_role}
                    </span>
                )}
            </div>
            <p
                className="mt-2 text-[12.5px] leading-relaxed line-clamp-3"
                style={{ color: "var(--atlas-text-tertiary)" }}
            >
                {skill.description || <i style={{ color: "var(--atlas-text-muted)" }}>No description.</i>}
            </p>
            {roleChips.length > 0 && (
                <div className="mt-2 flex items-center gap-1 flex-wrap">
                    {roleChips.map((r) => (
                        <span
                            key={r}
                            className="px-1.5 py-0.5 rounded-full text-[10px]"
                            style={{
                                background: "var(--atlas-bg-highlight)",
                                color: "var(--atlas-text-tertiary)",
                                border: "1px solid var(--atlas-border-strong)",
                            }}
                        >
                            {r}
                        </span>
                    ))}
                    {(skill.eligible_agent_roles || []).length > 3 && (
                        <span className="text-[10.5px]" style={{ color: "var(--atlas-text-muted)" }}>
                            +{(skill.eligible_agent_roles || []).length - 3}
                        </span>
                    )}
                </div>
            )}
            <div
                className="mt-auto pt-3 flex items-center justify-between gap-2"
                style={{ borderTop: "1px solid var(--atlas-border)" }}
            >
                <Link
                    to={`/app/skills/${skill.id}`}
                    data-testid={`skill-open-${skill.slug}`}
                    className="text-[12px] font-medium inline-flex items-center gap-1"
                    style={{ color: "var(--atlas-primary-hover)" }}
                >
                    {tab === "marketplace" || isSeed ? "View & install" : "Open"}
                    <ArrowRight className="w-3 h-3" strokeWidth={2} />
                </Link>
                <div className="flex items-center gap-2">
                    {canAuthor && tab === "my" && skill.status === "draft" && !isSeed && (
                        <button
                            onClick={activate}
                            disabled={running}
                            data-testid={`skill-activate-${skill.slug}`}
                            className="atlas-btn-primary inline-flex items-center gap-1 rounded-md px-2.5 h-7 text-[11.5px]"
                        >
                            <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} />
                            {running ? "Activating…" : "Activate"}
                        </button>
                    )}
                    {isPrivate && !isSeed && (
                        <span
                            className="text-[10px] inline-flex items-center gap-0.5"
                            style={{ color: "var(--atlas-text-muted)" }}
                        >
                            <Lock className="w-2.5 h-2.5" strokeWidth={2} /> private
                        </span>
                    )}
                    {canDelete && (
                        <button
                            onClick={() => setDeleteOpen(true)}
                            title="Delete skill"
                            aria-label="Delete skill"
                            data-testid="skill-delete-btn"
                            className="ml-1 inline-flex items-center justify-center w-7 h-7 rounded-md transition-all"
                            style={{
                                color: "var(--atlas-text-muted)",
                                background: "transparent",
                                border: "1px solid var(--atlas-border-strong)",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = "#fca5a5";
                                e.currentTarget.style.borderColor = "rgba(239,68,68,0.55)";
                                e.currentTarget.style.background = "rgba(239,68,68,0.10)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = "var(--atlas-text-muted)";
                                e.currentTarget.style.borderColor = "var(--atlas-border-strong)";
                                e.currentTarget.style.background = "transparent";
                            }}
                        >
                            <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                        </button>
                    )}
                </div>
            </div>
            <ConfirmDeleteModal
                open={deleteOpen}
                title="Delete this skill?"
                body={`"${skill.name}" will be deleted and any installations on your hired Workforce Members will be uninstalled. This can't be undone.`}
                confirmLabel="Delete skill"
                onConfirm={confirmDelete}
                onClose={() => setDeleteOpen(false)}
                busy={deleting}
            />
        </div>
    );
}
